import { useEffect, useState } from 'react';
import { AlertTriangle, ExternalLink } from 'lucide-react';
import { helpdeskApi, type ConnectionHealth } from '@/lib/api/helpdesk';

/** "36 days" / "5 hours" / "12 minutes" — enough to convey how long something has been wrong. */
function since(timestamp: string): string {
  const minutes = Math.max(0, Math.round((Date.now() - new Date(timestamp).getTime()) / 60000));
  if (minutes >= 2880) return `${Math.round(minutes / 1440)} days`;
  if (minutes >= 120) return `${Math.round(minutes / 60)} hours`;
  return `${minutes} minutes`;
}

function describe(connection: ConnectionHealth): string {
  const failures = connection.consecutive_failures > 0
    ? `${connection.consecutive_failures} failed ${connection.consecutive_failures === 1 ? 'attempt' : 'attempts'}`
    : null;
  const age = connection.last_success_at ? `last collected ${since(connection.last_success_at)} ago` : null;
  // The server states why it is unhealthy; the timings say how long it has been so.
  return [connection.reason, age, failures].filter(Boolean).join(' · ');
}

/**
 * Warn that a helpdesk integration has stopped collecting.
 *
 * Without this a broken connection is indistinguishable from a quiet one — the
 * tickets simply stop arriving, and the failures stay in a log table nobody reads.
 */
export function ConnectionHealthBanner() {
  const [unhealthy, setUnhealthy] = useState<ConnectionHealth[]>([]);

  useEffect(() => {
    let active = true;
    helpdeskApi.getConnectionHealth()
      .then(connections => { if (active) setUnhealthy(connections.filter(connection => !connection.healthy)); })
      .catch(() => { if (active) setUnhealthy([]); })
    return () => { active = false; };
  }, []);

  if (unhealthy.length === 0) return null;

  return (
    <div role="alert" className="mb-4 rounded-xl border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4">
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" aria-hidden />
        <div className="min-w-0">
          <p className="font-medium text-amber-900 dark:text-amber-200">
            {unhealthy.length === 1
              ? `${unhealthy[0].name} is not collecting tickets`
              : `${unhealthy.length} helpdesk integrations are not collecting tickets`}
          </p>
          <ul className="mt-1 space-y-1">
            {unhealthy.map(connection => (
              <li key={connection.config_id} className="text-sm text-amber-800 dark:text-amber-300">
                {unhealthy.length > 1 && <span className="font-medium">{connection.name}: </span>}
                {describe(connection)}
                {connection.sync_error && connection.sync_error !== connection.reason && (
                  <span className="block text-xs opacity-90">{connection.sync_error}</span>
                )}
              </li>
            ))}
          </ul>
          <a href="/settings/helpdesk"
            className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-amber-900 dark:text-amber-200 hover:underline">
            Check the integration
            <ExternalLink className="w-3 h-3" aria-hidden />
          </a>
        </div>
      </div>
    </div>
  );
}
